import React, { useState } from "react";

import CheckCircleIcon from "@material-ui/icons/CheckCircle";

import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

import CreateOutlinedIcon from "@material-ui/icons/CreateOutlined";

import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";

import upgardeloyalty from "apis/UpgradeLoyalty/upgardeloyalty";
import landinglogo from 'assets/img/hs_landing.png';
import bannerImage from 'assets/img/landingbanner.png';

import { toast } from "react-toastify";

// regular yearly price, used to work out the savings shown for a Yearly promo
const YEARLY_LIST_PRICE = 1299;

// Used only when the validate-promo API does not send discounted_price.
// Best fix: make the validate-promo API return discounted_price in res.data.
const PROMO_FALLBACK_PRICE = {
  TS300: 999,
};

// 999 -> "$999", 1299.5 -> "$1,299.50"
const formatMoney = (value) =>
  `$${Number(value).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

// price coming from the API (string / number / null) -> number or null
const toOfferPrice = (apiValue, code) => {
  const fromApi = Number(apiValue);

  if (apiValue !== null && apiValue !== undefined && apiValue !== "" && fromApi > 0) {
    return fromApi;
  }

  return PROMO_FALLBACK_PRICE[String(code || "").trim().toUpperCase()] ?? null;
};

export default function ThriveSignatureUpgrade({
  setNext,
  signatureData,
  setSignatureData
}) {
  const [formData, setFormData] = useState({
    title: "",
    ownerFirstName: "",
    ownerLastName: "",
    pocFirstName: "",
    pocLastName: "",
    email: "",
    officeNumber: "",
    promoCode: "",
    subscriptionPlan: "",
    signature: "",
  });

  const [loading, setLoading] = useState(false);

  const [isDrawing, setIsDrawing] = useState(false);

  const [hasSignature, setHasSignature] = useState(false);

  // promo state: promoInfo = { code, plan: 'Monthly' | 'Yearly' | 'Trial', trialPeriod, discountedPrice } once validated
  const [promoInfo, setPromoInfo] = useState(null);
  const [promoError, setPromoError] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);

  const {
    getcreationlanding,
    validatepromo,
  } = upgardeloyalty();

  /* =========================================================
     HANDLE CHANGE
  ========================================================= */

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /* =========================================================
     PROMO CODE
  ========================================================= */

  const handlePromoChange = (value) => {
    const wasForced = !!promoInfo;

    handleChange("promoCode", value);

    setPromoInfo(null);
    setPromoError("");

    // promo removed / edited -> release the forced plan selection
    if (wasForced) {
      handleChange("subscriptionPlan", "");
    }
  };

  // returns { ok, info } -> ok=false means invalid promo, blank promo is ok
  const applyPromo = async () => {
    const code = (formData.promoCode || "").trim();

    if (!code) {
      setPromoInfo(null);
      setPromoError("");
      return { ok: true, info: null };
    }

    if (promoInfo && promoInfo.inputCode === code) {
      return { ok: true, info: promoInfo };
    }

    setPromoLoading(true);

    try {
      const res = await validatepromo({
        promo_code: code,
        shipto: signatureData?.shipto || "",
        country: "US",
      });

      if (res?.result_status == 1 && res?.data) {
        const resolvedCode = res.data.promo_code || code;

        const info = {
          inputCode: code,
          code: resolvedCode,
          plan: res.data.plan,
          trialPeriod: res.data.trial_period ?? null,
          discountedPrice: toOfferPrice(res.data.discounted_price, resolvedCode),
        };

        setPromoInfo(info);
        setPromoError("");
        handleChange("subscriptionPlan", info.plan);

        return { ok: true, info };
      }

      setPromoInfo(null);
      setPromoError(
        res?.message ||
        "Invalid promo code. Please enter a valid promo code"
      );
      handleChange("subscriptionPlan", "");

      return { ok: false, info: null };
    } catch (e) {
      console.error(e);

      setPromoInfo(null);
      setPromoError("Unable to validate promo code. Please try again.");

      return { ok: false, info: null };
    } finally {
      setPromoLoading(false);
    }
  };

  /* =========================================================
     VALIDATION
  ========================================================= */

  const handleNameChange = (field, value) => {
    // Numbers and special characters are not allowed
    const cleanedValue = value.replace(
      /[^a-zA-Z\s'-]/g,
      ""
    );

    handleChange(field, cleanedValue);
  };

  const handleOfficeNumberChange = (value) => {
    // Numbers only
    const numbers = value
      .replace(/\D/g, "")
      .slice(0, 10);

    let formattedValue = numbers;

    if (
      numbers.length > 3 &&
      numbers.length <= 6
    ) {
      formattedValue = `(${numbers.slice(
        0,
        3
      )})-${numbers.slice(3)}`;
    } else if (numbers.length > 6) {
      formattedValue = `(${numbers.slice(
        0,
        3
      )})-${numbers.slice(3, 6)}-${numbers.slice(
        6
      )}`;
    }

    handleChange(
      "officeNumber",
      formattedValue
    );
  };

  const emailRegex =
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  /* =========================================================
     SIGNATURE
  ========================================================= */

  const getCanvasPoint = (e) => {
    const canvas = e.currentTarget;

    const rect =
      canvas.getBoundingClientRect();

    let clientX;
    let clientY;

    if (
      e.touches &&
      e.touches.length > 0
    ) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else if (
      e.changedTouches &&
      e.changedTouches.length > 0
    ) {
      clientX =
        e.changedTouches[0].clientX;

      clientY =
        e.changedTouches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x:
        ((clientX - rect.left) /
          rect.width) *
        canvas.width,

      y:
        ((clientY - rect.top) /
          rect.height) *
        canvas.height,
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();

    const canvas = e.currentTarget;

    const ctx = canvas.getContext("2d");

    const { x, y } =
      getCanvasPoint(e);

    ctx.beginPath();

    ctx.moveTo(x, y);

    setIsDrawing(true);
  };

  const drawSignature = (e) => {
    e.preventDefault();

    if (!isDrawing) return;

    const canvas = e.currentTarget;

    const ctx = canvas.getContext("2d");

    const { x, y } =
      getCanvasPoint(e);

    ctx.lineWidth = 2;

    ctx.lineCap = "round";

    ctx.lineJoin = "round";

    ctx.strokeStyle = "#1639CF";

    ctx.lineTo(x, y);

    ctx.stroke();

    setHasSignature(true);
  };

  const stopDrawing = (e) => {
    e.preventDefault();

    if (!isDrawing) return;

    const canvas = e.currentTarget;

    const signatureDataUrl =
      canvas.toDataURL("image/png");

    handleChange(
      "signature",
      signatureDataUrl
    );

    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas =
      document.getElementById(
        "signature-canvas"
      );

    if (!canvas) return;

    const ctx =
      canvas.getContext("2d");

    ctx.clearRect(
      0,
      0,
      canvas.width,
      canvas.height
    );

    handleChange(
      "signature",
      ""
    );

    setHasSignature(false);
  };

  /* =========================================================
     SUBMIT
  ========================================================= */

  const handleSubmit = async () => {
    if (!formData.title) {
      toast.error("Please select a title");
      return;
    }

    if (!formData.ownerFirstName) {
      toast.error(
        "Please enter Practice Owner First Name"
      );
      return;
    }

    if (!formData.ownerLastName) {
      toast.error(
        "Please enter Practice Owner Last Name"
      );
      return;
    }

    if (!formData.pocFirstName) {
      toast.error(
        "Please enter Point of Contact First Name"
      );
      return;
    }

    if (!formData.pocLastName) {
      toast.error(
        "Please enter Point of Contact Last Name"
      );
      return;
    }

    /* =========================
       EMAIL VALIDATION
    ========================= */

    if (!formData.email) {
      toast.error("Please enter Email");
      return;
    }

    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid Email");
      return;
    }

    /* =========================
       OFFICE NUMBER VALIDATION
    ========================= */

    if (!formData.officeNumber) {
      toast.error("Please enter Office Number");
      return;
    }

    if (
      !/^\(\d{3}\)-\d{3}-\d{4}$/.test(
        formData.officeNumber
      )
    ) {
      toast.error(
        "Please enter a valid Office Number"
      );
      return;
    }

    /* =========================
       PROMO CODE VALIDATION
    ========================= */

    const promoResult = await applyPromo();

    if (!promoResult.ok) {
      toast.error(
        "Invalid promo code. Please enter a valid promo code"
      );
      return;
    }

    const finalPlan =
      promoResult.info?.plan || formData.subscriptionPlan;

    if (!finalPlan) {
      toast.error(
        "Please select Subscription Plan"
      );
      return;
    }

    if (!formData.signature) {
      toast.error(
        "Please provide your signature"
      );
      return;
    }

    setLoading(true);

    try {
      const values = {
        shipto:
          signatureData?.shipto || "",

        practice_name:
          signatureData?.practice_name || "",

        city:
          signatureData?.city || "",

        state:
          signatureData?.state || "",

        title:
          formData.title,

        ownerfirstname:
          formData.ownerFirstName,

        ownerlastname:
          formData.ownerLastName,

        contactfirstname:
          formData.pocFirstName,

        contactlastname:
          formData.pocLastName,

        customeremail:
          formData.email,

        officenumber:
          formData.officeNumber,

        promo_code:
          (formData.promoCode || "").trim(),

        subscription_plan:
          finalPlan,

        trial_period:
          promoResult.info?.trialPeriod ?? null,

        signature:
          formData.signature,
      };

      const res =
        await getcreationlanding(values);

      if (res.result_status == 0) {
        setLoading(false);

        return toast.error(
          res.message
        );
      }

      if (res.result_status == 1) {
        setLoading(false);

        setSignatureData({
          shipto: values.shipto,
          practice_name: values.practice_name,
          city: values.city,
          state: values.state,
          title: values.title,
          ownerfirstname: values.ownerfirstname,
          ownerlastname: values.ownerlastname,
          contactfirstname: values.contactfirstname,
          contactlastname: values.contactlastname,
          customeremail: values.customeremail,
          officenumber: values.officenumber,
          promo_code: values.promo_code,
          subscription_plan: values.subscription_plan,
          trial_period: values.trial_period,
          geplink: signatureData?.GEPLink
        });
        setNext('2')

        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 100);
      }
    } catch (error) {
      console.error(error);

      toast.error(
        "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReturn = () => {
    setNext("")
  };

  const trialMonths = Number(promoInfo?.trialPeriod) || 0;

  // Yearly promo with a price -> same card layout as the normal Yearly plan:
  // title "Yearly Plan", bold big price "$999/year", description with the savings line
  const yearlyOfferPrice =
    promoInfo?.plan === "Yearly" && promoInfo?.discountedPrice
      ? Number(promoInfo.discountedPrice)
      : null;

  const yearlySavings =
    yearlyOfferPrice && YEARLY_LIST_PRICE > yearlyOfferPrice
      ? YEARLY_LIST_PRICE - yearlyOfferPrice
      : 0;

  return (
    <>

      <style>{responsiveCss}</style>

      <div className="upgrade-page">

        {/* =================================================
            TOP BANNER
        ================================================= */}

        <div className="top-banner">
          <img
            src={bannerImage}
            alt="Thrive Signature"
            className="top-banner-image"
          />

          <div className="banner-content">

            <div className="banner-brand">

              <img
                src={landinglogo}
                alt="Henry Schein"
                className="banner-hs-logo"
              />

            </div>

            <div className="banner-divider" />

            <div className="banner-tagline">
              Save More. Do More.{" "}
              <span>Thrive.</span>
            </div>

            <div className="banner-right">
              A HEALTHIER PRACTICE
              <br />
              A BRIGHTER TOMORROW
            </div>

          </div>

        </div>

        {/* =================================================
            MAIN CARD
        ================================================= */}

        <div className="upgrade-card">

          {/* =================================================
              STEPS
          ================================================= */}

          <div className="steps-wrapper">

            <UpgradeStep
              number="✓"
              title="Check Eligibility"
              active
            />

            <div className="step-connector active-line" />

            <UpgradeStep
              number="2"
              title="Review & Accept"
              active
            />

            <div className="step-connector" />

            <UpgradeStep
              number="3"
              title="Confirmation"
            />

          </div>

          {/* =================================================
              HEADING
          ================================================= */}

          <div className="upgrade-heading">

            <h1>
              Complete Your Thrive Signature Upgrade
            </h1>

            <p>
              You're eligible! Just a few more details
              to submit your enrollment.
            </p>

          </div>

          {/* =================================================
              ELIGIBILITY
          ================================================= */}

          <div className="eligibility-alert">

            <CheckCircleIcon
              className="success-icon"
            />

            <div>

              <div className="alert-title">
                Your practice is eligible for
                Thrive Signature.
              </div>

              <div className="alert-description">
                Complete the information below to review
                and accept your membership.
              </div>

            </div>

          </div>

          {/* =================================================
              YOUR INFORMATION
          ================================================= */}

          <section className="form-section">

            <h2>
              Your Information
            </h2>

            {/* TITLE */}

            <div className="field-row">

              <FormField
                label="Title"
                required
              >

                <div className="select-wrapper">

                  <select
                    value={formData.title}
                    onChange={(e) =>
                      handleChange(
                        "title",
                        e.target.value
                      )
                    }
                  >

                    <option value="">
                      Select a title (Dr., Mr., Ms., etc.)
                    </option>

                    <option value="Dr.">
                      Dr.
                    </option>

                    <option value="Mr.">
                      Mr.
                    </option>

                    <option value="Ms.">
                      Ms.
                    </option>

                    <option value="Mrs.">
                      Mrs.
                    </option>

                  </select>

                  <KeyboardArrowDownIcon />

                </div>

              </FormField>

            </div>

            {/* OWNER */}

            <div className="field-row two-column">

              <FormField
                label="Practice Owner First Name"
                required
              >

                <input
                  type="text"
                  placeholder="First Name"
                  value={
                    formData.ownerFirstName
                  }
                  onChange={(e) =>
                    handleNameChange(
                      "ownerFirstName",
                      e.target.value
                    )
                  }
                />

              </FormField>

              <FormField
                label="Practice Owner Last Name"
                required
              >

                <input
                  type="text"
                  placeholder="Last Name"
                  value={
                    formData.ownerLastName
                  }
                  onChange={(e) =>
                    handleNameChange(
                      "ownerLastName",
                      e.target.value
                    )
                  }
                />

              </FormField>

            </div>

          </section>

          {/* =================================================
              POINT OF CONTACT
          ================================================= */}

          <section className="form-section">

            <h2>
              Point of Contact
            </h2>

            <div className="field-row two-column">

              <FormField
                label="First Name"
                required
              >

                <input
                  type="text"
                  placeholder="First Name"
                  value={
                    formData.pocFirstName
                  }
                  onChange={(e) =>
                    handleNameChange(
                      "pocFirstName",
                      e.target.value
                    )
                  }
                />

              </FormField>

              <FormField
                label="Last Name"
                required
              >

                <input
                  type="text"
                  placeholder="Last Name"
                  value={
                    formData.pocLastName
                  }
                  onChange={(e) =>
                    handleNameChange(
                      "pocLastName",
                      e.target.value
                    )
                  }
                />

              </FormField>

              <FormField
                label="Email"
                required
              >

                <input
                  type="email"
                  placeholder="name@yourpractice.com"
                  value={formData.email}
                  onChange={(e) =>
                    handleChange(
                      "email",
                      e.target.value
                    )
                  }
                />

              </FormField>

              <FormField
                label="Office Number"
                required
              >

                <input
                  type="text"
                  placeholder="(000)-000-0000"
                  value={
                    formData.officeNumber
                  }
                  onChange={(e) =>
                    handleOfficeNumberChange(
                      e.target.value
                    )
                  }
                  maxLength={14}
                  inputMode="tel"
                />

              </FormField>

            </div>

          </section>

          {/* =================================================
              VERIFIED PRACTICE
          ================================================= */}

          <section className="form-section verified-section">

            <div className="section-heading-with-info">

              <h2>
                Verified Practice Information
              </h2>

              <InfoOutlinedIcon />

              <span>
                This information is from your account and
                cannot be edited.
              </span>

            </div>

            {/* PRACTICE NAME */}

            <VerifiedField
              label="Practice Name"
              value={
                signatureData?.practice_name || ""
              }
            />

            {/* CITY + STATE */}

            <div className="verified-three-column">

              <VerifiedField
                label="City"
                value={
                  signatureData?.city || ""
                }
              />

              <VerifiedField
                label="State"
                value={
                  signatureData?.state || ""
                }
              />

            </div>

          </section>

          {/* =================================================
              PROMO CODE
          ================================================= */}

          <section className="form-section promo-section">

            <h2>
              Promo Code (Optional)
            </h2>

            <div className="promo-row">

              <input
                type="text"
                placeholder="Enter promo code"
                value={
                  formData.promoCode
                }
                onChange={(e) =>
                  handlePromoChange(
                    e.target.value
                  )
                }
                onBlur={applyPromo}
                className={
                  promoError
                    ? "promo-input-error"
                    : promoInfo
                      ? "promo-input-success"
                      : ""
                }
              />

              <button
                type="button"
                className="promo-apply-button"
                onClick={applyPromo}
                disabled={
                  promoLoading ||
                  !(formData.promoCode || "").trim()
                }
              >
                {promoLoading
                  ? "Checking..."
                  : "Apply"}
              </button>

              <div className="promo-info">

                <InfoOutlinedIcon />

                <span>
                  If you have a promo code,
                  enter it here.
                </span>

              </div>

            </div>

            {promoError && (
              <div className="promo-message promo-error">
                {promoError}
              </div>
            )}

            {promoInfo && !promoError && (
              <div className="promo-message promo-success">
                Promo code applied.
              </div>
            )}

          </section>

          {/* =================================================
              SUBSCRIPTION
          ================================================= */}

          <section className="form-section">

            <h2>
              Select Subscription Plan
              <span className="required-star">
                *
              </span>
            </h2>

            <div
              className={
                promoInfo
                  ? "plans-row single-plan"
                  : "plans-row"
              }
            >

              {(!promoInfo || promoInfo.plan === "Monthly") && (
                <SubscriptionPlan
                  selected={
                    formData.subscriptionPlan ===
                    "Monthly"
                  }
                  onClick={() =>
                    !promoInfo &&
                    handleChange(
                      "subscriptionPlan",
                      "Monthly"
                    )
                  }
                  title="Monthly Plan"
                  price="$115 per month"
                  description={
                    <>
                      No commitment required.
                    </>
                  }
                />
              )}

              {(!promoInfo || promoInfo.plan === "Yearly") && (
                yearlyOfferPrice ? (
                  // promo applied: same layout, bold title + big bold price + description
                  <SubscriptionPlan
                    selected={
                      formData.subscriptionPlan ===
                      "Yearly"
                    }
                    onClick={() => { }}
                    title="Yearly Plan"
                    price={
                      `${formatMoney(yearlyOfferPrice)}/year` +
                      (yearlySavings > 0
                        ? ` \u2013 a ${formatMoney(yearlySavings)} Savings Limited time offer.`
                        : "")
                    }
                    description={
                      <>
                        Includes annual preventive
                        <br />
                        maintenance (labor only).
                        <br />
                        Yearly commitment required.
                      </>
                    }
                  />
                ) : (
                  <SubscriptionPlan
                    selected={
                      formData.subscriptionPlan ===
                      "Yearly"
                    }
                    onClick={() =>
                      !promoInfo &&
                      handleChange(
                        "subscriptionPlan",
                        "Yearly"
                      )
                    }
                    title="Yearly Plan"
                    price="$1,299 per year"
                    description={
                      <>
                        Includes annual preventive
                        <br />
                        maintenance (labor only).
                        <br />
                        Yearly commitment required.
                      </>
                    }
                  />
                )
              )}

              {promoInfo && promoInfo.plan === "Trial" && (
                <SubscriptionPlan
                  selected={
                    formData.subscriptionPlan ===
                    "Trial"
                  }
                  onClick={() => { }}
                  title="Trial Plan"
                  price={
                    trialMonths
                      ? `${trialMonths} Month${trialMonths > 1 ? "s" : ""} Free Trial`
                      : "Free Trial"
                  }
                  description={
                    <>
                      Complimentary trial period of
                      <br />
                      {trialMonths
                        ? `${trialMonths} month${trialMonths > 1 ? "s" : ""}.`
                        : "the promo duration."}
                    </>
                  }
                />
              )}

            </div>

          </section>

          {/* =================================================
              AGREEMENT
          ================================================= */}

          <section className="form-section agreement-section">

            <h2>
              Agreement & Signature
            </h2>

            <div className="agreement-text">

              By submitting your enrollment, you agree
              to the{" "}

              <a
                href={
                  signatureData?.thrivesignatureLink
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                Thrive Signature Service Agreement
              </a>{" "}

              and{" "}

              <a
                href={
                  signatureData?.thrivesignaturetermconditionLink
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms and Conditions
              </a>

            </div>

            {/* =================================================
                SIGNATURE CANVAS
            ================================================= */}

            <div className="signature-wrapper">

              <div className="signature-box">

                {!hasSignature && (
                  <div className="signature-placeholder">

                    <CreateOutlinedIcon />

                    <span>
                      Click to sign or draw your signature
                    </span>

                  </div>
                )}

                <canvas
                  id="signature-canvas"
                  className="signature-canvas"
                  width={680}
                  height={140}
                  onMouseDown={
                    startDrawing
                  }
                  onMouseMove={
                    drawSignature
                  }
                  onMouseUp={
                    stopDrawing
                  }
                  onMouseLeave={
                    stopDrawing
                  }
                  onTouchStart={
                    startDrawing
                  }
                  onTouchMove={
                    drawSignature
                  }
                  onTouchEnd={
                    stopDrawing
                  }
                />

              </div>

              {hasSignature && (
                <button
                  type="button"
                  className="clear-signature-button"
                  onClick={
                    clearSignature
                  }
                >
                  Clear Signature
                </button>
              )}

              <div className="signature-note">
                Date will be automatically recorded upon submission.
              </div>

            </div>

          </section>

          {/* =================================================
              BUTTONS
          ================================================= */}

          <div className="bottom-actions">

            <button
              type="button"
              className="back-button"
              onClick={handleReturn}
            >
              Back
            </button>

            <button
              type="button"
              className="submit-button"
              onClick={handleSubmit}
              disabled={loading || promoLoading}
            >
              {loading
                ? "Submitting..."
                : "Accept & Submit"}
            </button>

          </div>

        </div>

      </div>
    </>
  );
}


/* =========================================================
   STEP
========================================================= */

const UpgradeStep = ({
  number,
  title,
  active = false,
}) => {
  return (
    <div
      className={
        active
          ? "upgrade-step active"
          : "upgrade-step"
      }
    >
      <div className="upgrade-step-number">
        {number}
      </div>

      <div className="upgrade-step-title">
        {title}
      </div>
    </div>
  );
};


/* =========================================================
   FORM FIELD
========================================================= */

const FormField = ({
  label,
  required,
  children,
}) => {
  return (
    <div className="form-field">

      <label>

        {label}

        {required && (
          <span className="required-star">
            *
          </span>
        )}

      </label>

      {children}

    </div>
  );
};


/* =========================================================
   VERIFIED FIELD
========================================================= */

const VerifiedField = ({
  label,
  value,
}) => {
  return (
    <div className="verified-field">

      <label>
        {label}
      </label>

      <input
        type="text"
        value={value || ""}
        disabled
        readOnly
      />

    </div>
  );
};


/* =========================================================
   SUBSCRIPTION PLAN
   - every plan (normal or promo): title + big bold price + description
========================================================= */

const SubscriptionPlan = ({
  selected,
  onClick,
  title,
  price,
  description,
}) => {
  return (
    <div
      className={
        selected
          ? "subscription-plan selected"
          : "subscription-plan"
      }
      onClick={onClick}
    >

      <div
        className={
          selected
            ? "radio selected"
            : "radio"
        }
      >

        {selected && (
          <div className="radio-dot" />
        )}

      </div>

      <div className="plan-content">

        <div className="plan-title">
          {title}
        </div>

        <div className="plan-price">
          {price}
        </div>

        <div className="plan-description">
          {description}
        </div>

      </div>

    </div>
  );
};


/* =========================================================
   CSS
========================================================= */

const responsiveCss = `

* {
  box-sizing: border-box;
}

.upgrade-page {
  width: 100%;
  min-height: 100vh;
  margin: 0;
  padding: 0;
  font-family: Arial, Helvetica, sans-serif;
  color: #1127c9;
  background: #ffffff;
  overflow-x: hidden;
}


/* =========================================================
   TOP BANNER
========================================================= */

.top-banner {
  width: 100%;
  height: 110px;
  position: relative;
  overflow: hidden;
  background: none;
}

.top-banner-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
}

.banner-inner {
  position: relative;
  z-index: 2;
}

.banner-content {
  width: 100%;
  max-width: 760px;
  height: 68px;
  margin: 0 auto;
  padding: 0 0 0 28px;
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 12px;
}

.banner-brand {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-shrink: 0;
}

.banner-hs-logo {
  width: 106px;
  height: auto;
  object-fit: contain;
}

.banner-thrive-logo {
  width: 104px;
  height: auto;
  margin-top: -2px;
  object-fit: contain;
}

.banner-divider {
  width: 1px;
  height: 38px;
  background: #A9BFD8;
  flex-shrink: 0;
}

.banner-tagline {
  color: #1638CA;
  font-size: 17px;
  line-height: 22px;
  font-weight: 700;
  white-space: nowrap;
}

.banner-tagline span {
  color: #2F9BE8;
}

.banner-right {
  margin-left: 147px;
  color: #1744d8;
  font-size: 8px;
  line-height: 11px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-align: right;
  white-space: nowrap;
}


/* =========================================================
   MAIN CARD
========================================================= */

.upgrade-card {
  width: calc(100% - 30px);
  max-width: 760px;
  margin: -42px auto 0;
  padding: 18px 20px 16px;
  position: relative;
  z-index: 5;
  background: #FFFFFF;
  border: 1px solid #E3EDF8;
  border-radius: 5px;
  box-shadow:
    0 6px 24px rgba(25, 91, 170, 0.10);
}


/* =========================================================
   STEPS
========================================================= */

.steps-wrapper {
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  margin: 0 auto 14px;
}

.upgrade-step {
  width: 110px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.upgrade-step-number {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #E9EFFB;
  border: 1px solid #D5E0EF;
  color: #5A76D9;
  font-size: 11px;
  font-weight: 700;
}

.upgrade-step.active .upgrade-step-number {
  background: #166DE5;
  border-color: #166DE5;
  color: #FFFFFF;
  box-shadow:
    0 3px 8px rgba(22, 109, 229, 0.25);
}

.upgrade-step-title {
  margin-top: 5px;
  color: #6076C9;
  font-size: 9.5px;
  line-height: 12px;
  text-align: center;
  white-space: nowrap;
}

.upgrade-step.active .upgrade-step-title {
  color: #1765DE;
  font-weight: 700;
}

.step-connector {
  width: 100px;
  height: 1px;
  margin-top: 13px;
  background: #D1DCEC;
}

.step-connector.active-line {
  background: #6DA8EF;
}


/* =========================================================
   HEADING
========================================================= */

.upgrade-heading {
  text-align: center;
  margin: 0 auto 16px;
}

.upgrade-heading h1 {
  margin: 0 0 4px;
  color: #132BD2;
  font-size: 28px;
  line-height: 33px;
  font-weight: 700;
}

.upgrade-heading p {
  margin: 0;
  color: #4386E8;
  font-size: 14px;
  line-height: 19px;
}


/* =========================================================
   ELIGIBILITY
========================================================= */

.eligibility-alert {
  width: 100%;
  min-height: 56px;
  margin: 0 auto 18px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    #E4F8E9,
    #E9FAF0
  );
}

.success-icon {
  width: 30px !important;
  height: 30px !important;
  color: #54A963;
}

.alert-title {
  color: #0B65D7;
  font-size: 13px;
  line-height: 17px;
  font-weight: 700;
}

.alert-description {
  color: #4E78CC;
  font-size: 11.5px;
  line-height: 15px;
}


/* =========================================================
   FORM
========================================================= */

.form-section {
  width: 100%;
  margin: 0 0 16px;
}

.form-section h2 {
  margin: 0 0 8px;
  color: #102ACB;
  font-size: 15px;
  line-height: 19px;
  font-weight: 700;
}

.required-star {
  color: #ED3E3E;
  margin-left: 2px;
}

.field-row {
  width: 100%;
  display: flex;
  gap: 20px;
}

.field-row.two-column {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 20px;
  row-gap: 12px;
}

.form-field {
  width: 100%;
}

.form-field label,
.verified-field label {
  display: block;
  margin: 0 0 5px;
  color: #1639CF;
  font-size: 11px;
  line-height: 14px;
  font-weight: 700;
}

.form-field input,
.form-field select,
.select-wrapper select {
  width: 100%;
  height: 36px;
  padding: 8px 12px;
  border: 1px solid #C9D8F1;
  border-radius: 4px;
  outline: none;
  background: #FFFFFF;
  color: #536DA8;
  font-size: 12px;
  font-family: Arial, Helvetica, sans-serif;
}

.form-field input::placeholder {
  color: #7184BD !important;
}

.form-field input:focus,
.form-field select:focus {
  border-color: #3989ED;
}


/* =========================================================
   SELECT
========================================================= */

.select-wrapper {
  position: relative;
  width: 320px;
  max-width: 100%;
}

.select-wrapper select {
  appearance: none;
  padding-right: 32px;
}

.select-wrapper svg {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  color: #3267D5;
  pointer-events: none;
}


/* =========================================================
   VERIFIED PRACTICE
========================================================= */

.verified-section {
  margin-top: 4px;
}

.section-heading-with-info {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 8px;
}

.section-heading-with-info h2 {
  margin: 0;
}

.section-heading-with-info svg {
  width: 17px;
  height: 17px;
  color: #1C3BD3;
}

.section-heading-with-info span {
  color: #758BC1;
  font-size: 11px;
  line-height: 14px;
}

.verified-field {
  width: 100%;
  margin-bottom: 10px;
}

.verified-field input {
  width: 100%;
  height: 36px;
  padding: 8px 12px;
  border: 1px solid #DCE1E9;
  border-radius: 4px;
  background: #E7EBF0;
  color: #8294BB;
  font-size: 12px;
  outline: none;
}

.verified-three-column {
  width: 100%;
  display: flex;
  gap: 20px;
}

.verified-three-column > * {
  flex: 1;
  min-width: 0;
}

.verified-three-column .verified-field {
  margin-bottom: 0;
}


/* =========================================================
   PROMO
========================================================= */

.promo-row {
  display: flex;
  align-items: center;
  gap: 20px;
}

.promo-row > input {
  width: 50%;
  height: 36px;
  padding: 8px 12px;
  border: 1px solid #C9D8F1;
  border-radius: 4px;
  outline: none;
  color: #536DA8;
  font-size: 12px;
}

.promo-row > input::placeholder {
  color: #7184BD !important;
}

.promo-row > input.promo-input-error {
  border-color: #ED3E3E;
}

.promo-row > input.promo-input-success {
  border-color: #54A963;
}

.promo-apply-button {
  height: 36px;
  padding: 0 16px;
  border: 1px solid #65A7FA;
  border-radius: 4px;
  background: #FFFFFF;
  color: #1670E9;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  margin-left: -8px;
}

.promo-apply-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.promo-message {
  margin-top: 6px;
  font-size: 11px;
  line-height: 14px;
}

.promo-error {
  color: #ED3E3E;
}

.promo-success {
  color: #2E8B47;
}

.promo-info {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #6682D0;
  font-size: 11px;
  line-height: 14px;
}

.promo-info svg {
  width: 17px;
  height: 17px;
  color: #263FD5;
}


/* =========================================================
   PLANS
========================================================= */

.plans-row {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.plans-row.single-plan {
  grid-template-columns: 1fr;
}

.subscription-plan {
  min-height: 96px;
  padding: 14px 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  border: 1px solid #CCDAFA;
  border-radius: 5px;
  background: #FFFFFF;
  cursor: pointer;
}

.subscription-plan.selected {
  border-color: #2879E8;
  box-shadow:
    0 0 0 1px rgba(40, 121, 232, 0.10);
}

.radio {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  margin-top: 1px;
  border-radius: 50%;
  border: 1px solid #AABDE7;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #FFFFFF;
}

.radio.selected {
  border-color: #5C8AE8;
}

.radio-dot {
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #1B72ED;
}

.plan-content {
  min-width: 0;
}

.plan-title {
  color: #1A3FD1;
  font-size: 12px;
  line-height: 15px;
  font-weight: 700;
}

.plan-price {
  margin-top: 2px;
  color: #102DD1;
  font-size: 23px;
  line-height: 26px;
  font-weight: 700;
}

.plan-description {
  margin-top: 2px;
  color: #7184BD;
  font-size: 11px;
  line-height: 14px;
}


/* =========================================================
   AGREEMENT
========================================================= */

.agreement-section {
  margin-top: 4px;
  margin-bottom: 14px;
}

.agreement-text {
  color: #7184B9;
  font-size: 11px;
  line-height: 15px;
  margin-bottom: 10px;
}

.agreement-text a {
  color: #2769D9;
  text-decoration: underline;
}


/* =========================================================
   SIGNATURE
========================================================= */

.signature-wrapper {
  width: 100%;
}

.signature-box {
  position: relative;
  width: 100%;
  height: 140px;
  border: 1px dashed #7184BD;
  background: #FFFFFF;
  overflow: hidden;
}

.signature-canvas {
  display: block;
  width: 100%;
  height: 100%;
  cursor: crosshair;
  touch-action: none;
}

.signature-placeholder {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  color: #7184BD;
  font-size: 12px;
  pointer-events: none;
  white-space: nowrap;
}

.signature-placeholder svg {
  width: 20px;
  height: 20px;
  color: #3C6BD7;
}

.clear-signature-button {
  display: block;
  margin: 6px 0 0 auto;
  padding: 4px 10px;
  border: 1px solid #C9D8F1;
  border-radius: 4px;
  background: #FFFFFF;
  color: #2769D9;
  font-size: 10px;
  cursor: pointer;
}

.clear-signature-button:hover {
  background: #F2F7FF;
}

.signature-note {
  text-align: center;
  margin-top: 6px;
  color: #8B9BC2;
  font-size: 10.5px;
  line-height: 13px;
}


/* =========================================================
   BUTTONS
========================================================= */

.bottom-actions {
  width: 100%;
  padding-top: 14px;
  border-top: 1px solid #E1E9F5;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.back-button,
.submit-button {
  height: 40px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.back-button {
  width: 130px;
  background: #FFFFFF;
  border: 1px solid #65A7FA;
  color: #1670E9;
}

.submit-button {
  width: 170px;
  border: none;
  background: #0878EC;
  color: #FFFFFF;
  box-shadow:
    0 2px 5px rgba(0, 105, 225, 0.18);
}

.submit-button:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}


/* =========================================================
   MOBILE
========================================================= */

@media only screen and (max-width: 600px) {

  .top-banner {
    height: 92px;
  }

  .banner-content {
    width: 100%;
    max-width: none;
    height: 72px;
    padding: 0 10px;
    display: flex;
    align-items: center;
    gap: 7px;
  }

  .banner-brand {
    flex-shrink: 0;
  }

  .banner-hs-logo {
    width: 50px;
  }

  .banner-thrive-logo {
    width: 82px;
  }

  .banner-divider {
    width: 1px;
    height: 30px;
    flex-shrink: 0;
  }

  .banner-tagline {
    flex: 0 0 auto;
    font-size: 12px;
    line-height: 16px;
    white-space: nowrap;
  }

  .banner-right {
    margin-left: auto;
    flex-shrink: 0;
    display: block;
    font-size: 6px;
    line-height: 8px;
    letter-spacing: 0.3px;
    text-align: right;
    white-space: nowrap;
  }

  .upgrade-card {
    width: calc(100% - 14px);
    margin: -34px auto 20px;
    padding: 12px 10px;
  }

  .upgrade-step {
    width: 80px;
  }

  .step-connector {
    width: 38px;
  }

  .upgrade-heading h1 {
    font-size: 19px;
    line-height: 24px;
  }

  .upgrade-heading p {
    font-size: 10.5px;
    line-height: 14px;
  }

  .field-row.two-column {
    grid-template-columns: 1fr;
  }

  .verified-three-column {
    display: flex;
    gap: 8px;
  }

  .verified-three-column > * {
    flex: 1;
  }

  .promo-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .promo-row > input {
    width: 100%;
  }

  .promo-apply-button {
    margin-left: 0;
  }

  .plans-row {
    grid-template-columns: 1fr;
  }

  .signature-box {
    height: 130px;
  }

  .signature-canvas {
    width: 100%;
    height: 100%;
    touch-action: none;
  }

  .signature-placeholder {
    font-size: 10px;
    white-space: nowrap;
  }

  .signature-placeholder svg {
    width: 17px;
    height: 17px;
  }

  .bottom-actions {
    padding-top: 10px;
  }

  .back-button {
    width: 100px;
  }

  .submit-button {
    width: 130px;
  }
}

`;